// frontend/components/bigquery/BigQueryDatasetCreator.jsx
import React, { useState } from 'react';
import { 
  TextField, 
  Box, 
  Typography, 
  Collapse, 
  Button, 
  CircularProgress,
  Alert,
  Link
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { getCsrfToken } from 'utils/csrf';

const BigQueryDatasetCreator = ({ 
  projectId, 
  onDatasetCreated, 
  buttonText = "Need to create a new dataset? Click here!" 
}) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newDatasetName, setNewDatasetName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState(false);

  // Toggle the create form visibility
  const toggleCreateForm = () => {
    setShowCreateForm(!showCreateForm);
    if (showCreateForm) {
      // Reset form when closing
      setNewDatasetName('');
      setCreateError('');
      setCreateSuccess(false);
    }
  };

  // Handle dataset name input
  const handleDatasetNameChange = (e) => {
    setNewDatasetName(e.target.value);
    setCreateError(''); // Clear previous errors
  };

  // Create the new dataset
  const handleCreateDataset = async () => {
    // Validate dataset name (BigQuery naming rules)
    if (!newDatasetName.match(/^[a-zA-Z0-9_]+$/)) {
      setCreateError('Dataset name can only contain letters, numbers, and underscores');
      return;
    }

    setIsCreating(true);
    setCreateError('');
    try {
      const response = await fetch('/bigquery/dataset/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCsrfToken(),
        },
        body: JSON.stringify({
          project_id: projectId,
          dataset_id: newDatasetName,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create dataset');
      }

      // Success
      setCreateSuccess(true);
      
      // Notify parent component
      if (onDatasetCreated) {
        onDatasetCreated(newDatasetName);
      }
      
      // Auto-hide the form after a short delay
      setTimeout(() => {
        setShowCreateForm(false);
        setNewDatasetName('');
      }, 1000);
    } catch (error) {
      setCreateError(error.message);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>
      {/* Create dataset link */}
      <Box sx={{ mb: 2 }}>
        <Link 
          component="button" 
          variant="body2" 
          onClick={toggleCreateForm}
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            color: 'primary.main',
            textDecoration: 'none', 
            '&:hover': { textDecoration: 'underline' }
          }}
        >
          <AddIcon fontSize="small" sx={{ mr: 0.5 }} />
          {showCreateForm ? 'Cancel' : buttonText}
        </Link>
      </Box>
      
      {/* Collapsible create dataset form */}
      <Collapse in={showCreateForm}>
        <Box 
          sx={{ 
            p: 2, 
            mb: 2, 
            borderRadius: 1, 
            border: '1px solid', 
            borderColor: 'divider',
            bgcolor: 'background.paper' 
          }}
        >
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Create a new BigQuery dataset
          </Typography>
          
          <TextField
            fullWidth
            label="New dataset name"
            value={newDatasetName}
            onChange={handleDatasetNameChange}
            placeholder="my_new_dataset"
            size="small"
            margin="normal"
            helperText="Dataset names can only contain letters, numbers, and underscores"
            error={Boolean(createError)}
          />
          
          {createError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {createError}
            </Alert>
          )}
          
          {createSuccess && (
            <Alert severity="success" sx={{ mt: 1 }}>
              Dataset created successfully!
            </Alert>
          )}
          
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={toggleCreateForm}
              color="primary"
            >
              Never mind!
            </Button>
            
            <Button
              variant="contained"
              onClick={handleCreateDataset}
              disabled={!newDatasetName || isCreating}
              startIcon={isCreating ? <CircularProgress size={20} /> : null}
              color="primary"
            >
              {isCreating ? 'Creating...' : 'Create Dataset'}
            </Button>
          </Box>
        </Box>
      </Collapse>
    </>
  );
};

export default BigQueryDatasetCreator;